import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import QuestionDisplay from "../../../views/athlete-portal/profile/components/QuestionDisplay";
import QuestionCreate from "../../../views/athlete-portal/profile/components/QuestionCreate";
import {
  isUnchanged,
  validateCreateQuestion,
  validateCreateTemplate,
} from "../../../utils/questionnaire-validations";
import submit from "submit-form";
import SvgIcon from "../../SvgIcon";
import scriptLoader from "../../../utils/scriptLoader";

const AddQuestionnaireTemplate = (
  { template, userToLoadInformation, organizationOwner },
  { locals }
) => {
  const [questions, setQuestions] = useState(
    template ? template.questions : []
  );

  const [adding, setAdding] = useState(template ? false : true);
  const [name, setName] = useState(template ? template.title : "");
  const [addNew, setAddNew] = useState(true);
  const [firstEdition, setFirstEdition] = useState(template ? true : false);

  const [asDefault, setAsDefault] = useState(false);
  const [edit, setEdit] = useState(false);
  const [editedQuestion, setEditedQuestion] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState({
    title: "",
    type: "short",
    body: { description: "Short Answer" },
  });
  const [questionOrder, setQuestionOrder] = useState(null);

  const { assert, flash } = locals;

  const handleSetDefault = () => {
    setAsDefault(!asDefault);
  };

  const handleNameChange = (e) => {
    const { value } = e.target;

    setName(value);
  };

  useEffect(() => {
    if (!firstEdition && !adding) {
      setEditedQuestion(null);
      setCurrentQuestion({ title: "", type: "", body: {} });
      setAdding(true);
    }
    if (questions.length === 0) {
      setAddNew(true);
    }
  }, [questions]);

  useEffect(() => {
    if (document.querySelector(".principal-grabber-div-template")) {
      scriptLoader(
        "https://cdn.jsdelivr.net/npm/sortablejs@latest/Sortable.min.js",
        () => {
          const dragImage = document.createElement("img");
          dragImage.src =
            "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
          const sortable = new Sortable(
            document.querySelector(".principal-grabber-div-template"),
            {
              animation: 350,
              handle: ".drag-handle-template",
              setData(dataTransfer) {
                dataTransfer.setDragImage(dragImage, 0, 0);
              },
              onEnd: (e) => {
                if (e.oldIndex === e.newIndex) return;

                const newOrderedQuestions = Array.from(
                  document.querySelectorAll(".item-grabber-container-template")
                ).map((question) => question.id);

                setQuestionOrder(newOrderedQuestions);
              },
            }
          );
        }
      );
    }
  }, []);

  const handleAddNew = () => {
    setFirstEdition(false);
    setAdding(true);
  };

  const handleSaveQuestion = () => {
    const isEditedValid = edit
      ? validateCreateQuestion(editedQuestion, assert, flash)
      : true;
    const isCreateValid = adding
      ? validateCreateQuestion(currentQuestion, assert, flash)
      : true;

    if (isCreateValid && isEditedValid) {
      const newQuestion = {
        ...currentQuestion,
        _id: `${currentQuestion.title}-${questions.length}`,
      };

      const updatedQuestions = edit
        ? getEditedQuestions(editedQuestion, newQuestion, adding)
        : [...questions, newQuestion];

      setQuestions(updatedQuestions);
      setEdit(false);
      setAdding(false);
      setAddNew(true);
    }
  };

  const getEditedQuestions = (editedQuestion, newQuestion, isCreateValid) => {
    const editedQuestions = questions.map((question) =>
      question._id === editedQuestion._id ? editedQuestion : question
    );

    const result = isCreateValid
      ? [...editedQuestions, { ...newQuestion }]
      : editedQuestions;

    return result;
  };

  const onEdit = (id) => {
    const question = questions.find((question) => question._id === id);
    setEditedQuestion(question);
    setEdit(true);
    setFirstEdition(false);
  };

  const onDelete = (id) => {
    const newQuestions = questions.filter((question) => question._id !== id);

    setQuestions(newQuestions);

    if (!newQuestions.length) {
      setEdit(false);
      setAdding(true);
      setAddNew(false);
    }
  };

  const getQuestionsArray = () => {
    const isCurrentQuestionUnchanged = isUnchanged(currentQuestion);
    const isEditedQuestionValid =
      editedQuestion && validateCreateQuestion(editedQuestion, assert, flash);
    const isCurrentQuestionValid =
      !isCurrentQuestionUnchanged &&
      validateCreateQuestion(currentQuestion, assert, flash);

    if (isCurrentQuestionUnchanged) {
      if (isEditedQuestionValid) {
        return getEditedQuestions(editedQuestion, {}, false);
      } else {
        return [...questions];
      }
    } else {
      if (isCurrentQuestionValid) {
        if (isEditedQuestionValid) {
          return getEditedQuestions(editedQuestion, currentQuestion, true);
        } else {
          return [...questions, { ...currentQuestion }];
        }
      } else {
        return false;
      }
    }
  };

  const handleSaveTemplate = () => {
    const questionsArray = getQuestionsArray();

    if (!questionsArray) return false;

    let orderedQuestions = [...questionsArray];

    if (questionOrder) {
      const orderMap = new Map(questionOrder.map((id, index) => [id, index]));

      // Just in case someone has a question being created, it should go to the bottom of the array
      orderedQuestions = [...questionsArray].sort((a, b) => {
        const indexA = orderMap.has(a._id) ? orderMap.get(a._id) : Infinity;
        const indexB = orderMap.has(b._id) ? orderMap.get(b._id) : Infinity;
        return indexA - indexB;
      });
    }

    const newQuestions = orderedQuestions.map((question) => ({
      title: question.title,
      type: question.type,
      body: { ...question.body },
    }));

    const newTemplate = {
      title: name,
      questions: [...newQuestions],
    };

    if (validateCreateTemplate(newTemplate, assert, flash)) {
      const url = template
        ? `/actions/questionnaire/add-template?templateId=${template._id}`
        : `/actions/questionnaire/add-template`;

      submit(url, {
        method: "POST",
        body: {
          title: newTemplate.title,
          questions: newTemplate.questions,
          isDefault: asDefault,
          templateId: template ? template._id : null,
          userToRequest: organizationOwner?._id || userToLoadInformation?._id,
        },
      });
    }
  };

  return (
    <div className="Modal-Box add-checkin-template-modal">
      <a href={`/actions/refresh?modal=&templateId`} className="overlay" />
      <div className="box">
        <header>
          <h2 className="only-show-mobile">
            {template ? "Edit a Template" : "Add a Template"}
          </h2>
          <input
            type="text"
            className="input-template-title mobile-hidden"
            placeholder="ENTER TITLE HERE"
            value={name}
            name="template-name"
            onChange={handleNameChange}
          />
          <a
            href={`/actions/refresh?modal=&templateId=`}
            role="button"
            data-button-type="tertiary"
            data-icon-only="true"
          >
            <img src="/img/new-icons/red-cross.svg" alt="dismiss" />
          </a>
        </header>

        <div className="body">
          <label className="force-only-show-mobile">
            <p className="p4">Template Name</p>
            <input
              name="template-name"
              className="input-template-title"
              placeholder="Enter title here"
              value={name}
              onChange={handleNameChange}
            />
          </label>
          <div className="questionnaire-add-template-questions">
            <div className="principal-grabber-div-template">
              {!!questions.length &&
                questions.map((question, index) => {
                  if (editedQuestion && question._id === editedQuestion._id) {
                    return (
                      <QuestionCreate
                        data={editedQuestion}
                        setData={setEditedQuestion}
                        isEdited={true}
                        key={`${question._id}`}
                      />
                    );
                  }

                  return (
                    <div
                      className="item-grabber-container-template"
                      id={question._id}
                      key={`${question._id}-template`}
                    >
                      <QuestionDisplay
                        body={question.body}
                        title={question.title}
                        type={question.type}
                        id={question._id}
                        onEdit={onEdit}
                        onDelete={onDelete}
                        key={`${question._id}`}
                      />

                      <div className="drag-handle-template">
                        <SvgIcon src="/img/new-icons/menu.svg" alt="grab" />
                      </div>
                    </div>
                  );
                })}
            </div>

            {adding && (
              <QuestionCreate
                data={currentQuestion}
                setData={setCurrentQuestion}
              />
            )}

            {addNew && (
              <div className="template-modal-buttons add-acc-new-footer-buttons">
                <button
                  data-button-type="outline"
                  onClick={firstEdition ? handleAddNew : handleSaveQuestion}
                >
                  Add New
                </button>
                <a
                  href={`/actions/refresh?modal=&templateId=`}
                  className=" only-show-mobile acc-btn add-new"
                >
                  Cancel
                </a>
              </div>
            )}
          </div>
        </div>

        <footer>
          <div onClick={handleSetDefault}>
            <SvgIcon
              src={`/img/new-icons/${asDefault ? "" : "un"}checked-square.svg`}
              noFilterOnLight={asDefault ? true : false}
            />
            <p className="p4">Set as default</p>
          </div>

          <button onClick={handleSaveTemplate} data-button-type="primary">
            Save
          </button>
        </footer>
      </div>
    </div>
  );
};

AddQuestionnaireTemplate.contextTypes = { locals: PropTypes.object };

export default AddQuestionnaireTemplate;
